import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { ConfigProvider } from 'antd';
import styled from 'styled-components';
import { space, color, border } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { useSelector, useDispatch } from 'react-redux';
import { changeFilters } from 'redux-api/action/filters';
import calculateRange from 'lib/calculate-range';
import ButtonBorder from 'theme/buttons/btn-border';
import Button from 'theme/buttons/btn';
import Text from 'theme/text';
import Select from 'theme/select';
import RangePicker from 'theme/range-picker';
import translations from 'translations';
import itIT from 'antd/lib/locale/it_IT';
import enUS from 'antd/lib/locale/en_US';
import deDE from 'antd/lib/locale/de_DE';

const Wrapper = styled.div`
  position: relative;
  flex: 1;

  & > .button-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 1;

    & > .column {
      display: flex;
      flex-direction: column;
      margin-right: ${themeGet('space.s')}px;

      & > .flex {
        display: flex;
        align-items: center;
      }
    }
  }
`;

const Menu = styled.div`
  ${space}
  ${color}

  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};

  width: 596px;
  position: absolute;
  top: 52px;
  border: 2px solid;
  border-radius: 12px;
  z-index: 999;

  ${({ direction }) => (direction === 'right' ? 'right: 0' : 'left: 0')};

  ${border}

  & > .row {
    display: flex;
    flex-direction: row;

    & > div {
      flex: 1;
    }

    & > :first-child {
      margin-right: ${themeGet('space.s')}px;
    }
  }

  & > .buttons {
    padding-top: ${themeGet('space.l')}px;
    justify-content: space-between;
  }
`;

function DateSelector({
  direction,
  compare,
  dataType,
  dateRange,
  selectByFilter,
  dates,
}) {
  const dispatch = useDispatch();
  const ref = useRef();

  const [isOpen, setOpen] = useState(false);
  const [filtersState, setFiltersState] = useState({
    compare,
    dataType,
    dateRange,
    dates,
  });

  useEffect(() => {
    setFiltersState({
      compare,
      dataType,
      dateRange,
      dates,
    });
  }, [compare, dataType, dateRange, dates]);

  const { languageCode } = useSelector((state) => state.config);

  const dateRangeValue = useMemo(() => {
    const result = selectByFilter.find((el) => el.value === dateRange);

    if (result && result.label) {
      return result.label;
    }

    return 'Custom';
  }, [dateRange, selectByFilter]);

  const handleOpen = useCallback(() => {
    setOpen(!isOpen);
  }, [isOpen]);

  const handleCancel = useCallback(() => {
    setFiltersState({
      compare,
      dataType,
      dateRange,
      dates,
    });
    setOpen(false);
  }, [compare, dataType, dateRange, dates]);

  const handleApply = useCallback(() => {
    setOpen(false);
    dispatch(changeFilters(filtersState));
  }, [filtersState, dispatch]);

  const handleChangeCompare = useCallback(
    (payload) => {
      setFiltersState({ ...filtersState, compare: payload });
    },
    [filtersState],
  );

  const handleChangeDateRange = useCallback(
    (payload) => {
      const calculatedDates = calculateRange(payload);

      setFiltersState({ ...filtersState, dateRange: payload, dates: calculatedDates });
    },
    [filtersState],
  );

  const handleChangeDates = useCallback(
    (payload) => {
      if (!payload) {
        const defaultRange = selectByFilter[0].value;
        const defaultDates = calculateRange(defaultRange);

        setFiltersState({ ...filtersState, dates: defaultDates });
      } else {
        setFiltersState({ ...filtersState, dateRange: 'custom', dates: payload });
      }
    },
    [filtersState, selectByFilter],
  );

  const getLocale = useCallback(() => {
    switch (languageCode) {
      case 'it': return itIT;
      case 'de': return deDE;
      default: return enUS;
    }
  }, [languageCode]);

  return (
    <ConfigProvider locale={getLocale()}>
      <Wrapper ref={ref}>
        <div className="button-wrapper">
          <ButtonBorder
            onClick={handleOpen}
            style={{
              width: '100%',
              maxWidth: '190px',
              height: '48px',
              p: 0,
              mr: 's',
              color: 'grey_400',
            }}
            type="button"
            label={dateRangeValue}
          />
        </div>

        <Menu
          direction={direction}
          bg="white"
          isVisible={isOpen}
          px="s"
          py="m"
          borderColor="primary_200"
          mt="10px"
        >
          <Text fontSize="label" lineHeight="label" fontWeight="bold" color="grey_600" mb="s">
            {translations[languageCode]['date range']}
          </Text>

          <Select
            meta={{}}
            input={{
              value: filtersState.dateRange,
              onChange: handleChangeDateRange,
            }}
            defaultValue={selectByFilter[0].value}
            options={selectByFilter}
            noError
            style={{
              mb: 's',
            }}
          />

          <Text fontSize="label" lineHeight="label" fontWeight="bold" color="grey_600" mb="s">
            {translations[languageCode]['specific date']}
          </Text>
          <RangePicker
            languageCode={languageCode}
            value={filtersState.dates}
            onChange={handleChangeDates}
          />

          <div className="row">
            <div>
              <Text fontSize="label" lineHeight="label" fontWeight="bold" color="grey_600" mb="s">
                {translations[languageCode]['compare to']}
              </Text>
              <Select
                meta={{}}
                input={{
                  value: filtersState.compare,
                  onChange: handleChangeCompare,
                }}
                options={[
                  {
                    label: translations[languageCode].period,
                    value: 'period',
                  },
                  {
                    label: translations[languageCode].year,
                    value: 'year',
                  },
                ]}
                noError
              />
            </div>
          </div>

          <div className="row buttons">
            <ButtonBorder
              onClick={handleCancel}
              style={{
                width: '100%',
                maxWidth: '110px',
                minWidth: '110px',
                height: '48px',
                p: 0,
                color: 'grey_400',
              }}
              type="button"
              label={translations[languageCode].cancel}
            />

            <Button
              onClick={handleApply}
              style={{
                width: '100%',
                maxWidth: '110px',
                minWidth: '110px',
                height: '48px',
                p: 0,
              }}
              type="button"
              label={translations[languageCode].apply}
            />
          </div>
        </Menu>
      </Wrapper>
    </ConfigProvider>
  );
}

export default DateSelector;
